import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import MapMarker from "components/icons/MapMarker";
import Mail from "components/icons/Mail";
import CallIcon from "components/icons/Call";
import Calendar from "components/icons/Calendar";
import Button from "components/components/atoms/Button";
import breakpoints from "utils/breakpoints";
import useBreakpoint from "utils/hooks/useBreakpoint";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "tailwind.config.js";
import cx from "classnames";
import getMapLink from "utils/getMapLink";
import { useSpring, animated } from "react-spring";
import { useMeasure } from "react-use";

const fullConfig = resolveConfig(tailwindConfig);
const CardContainer = styled.div`
  margin-top: 0.313rem;
  margin-bottom: 0.25rem;
  @media ${breakpoints.md} {
    margin-bottom: 0.25rem;
  }
`;

const Card = styled.div`
  margin-top: 1rem;
  margin-bottom: 2rem;
  @media ${breakpoints.md} {
    margin-bottom: 1rem;
  }
`;

const CardTitle = styled.h3`
  letter-spacing: -0.029rem;
`;

const CardDescription = styled.div.attrs({
  className:
    "font-graphie text-mattamy-blue text-base leading-relaxed md:leading-5  md:mb-0 flex items-start",
})`
  letter-spacing: 0.007rem;
`;

const ToggleScheduleContainer = styled.div`
  place-self: center;
`;

const ProductInfo = styled.div`
  @media ${breakpoints.lg} {
    min-height: 10rem;
  }
`;

const IconContainer = styled.div.attrs({
  className:
    "flex justify-center items-center w-30-mattamy h-30-mattamy text-action-blue inline ml-4 mr-2 rounded-full bg-white",
})`
  min-width: 1.875rem;
`;

const StyledButton = styled(Button)`
  height: 3.125rem;
`;

const DesingCenterCommunityCardMobile = (props) => {
  const {
    city,
    state,
    designStudioName,
    direction,
    emailAddress,
    phone,
    designStudioHours,
    zipCode,
    type,
    description,
    ctaText = "texto CTA",
    ctaurl,
    openNow,
    gmbLink = {},
  } = props;
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const defaultHeight = "0px";
  const [contentHeight, setContentHeight] = useState(defaultHeight);
  const [scheduleOpen, toggleScheduleOpen] = useState(false);
  const [scheduleRefHeight, { height }] = useMeasure();
  const getProperHeight = () => {
    if (scheduleOpen) {
      return `${contentHeight}px`;
    }
    return "0px";
  };

  const expand = useSpring({
    height: getProperHeight(),
    pointerEvents: scheduleOpen ? "all" : "none",
  });
  const fade = useSpring({
    opacity: scheduleOpen ? 1 : 0,
    delay: scheduleOpen ? 200 : 0,
  });
  useEffect(() => {
    setContentHeight(height);
    window.addEventListener("resize", setContentHeight(height));

    return window.removeEventListener("resize", setContentHeight(height));
  }, [height]);

  const getClosedHour = () => {
    const weekdays = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];
    const d = new Date();
    let day = d.getDay();
    const hours = designStudioHours.find((h) =>
      h.day.includes(weekdays[day - 1])
    );
    const chour = hours && hours.hours && hours.hours.split("-");
    return chour && chour.length > 1 ? chour[1] : "";
  };
  const renderSchedule = () => (
    <animated.div
      className="flex flex-col items-center justify-start w-full"
      ref={scheduleRefHeight}
      style={fade}
    >
      <>
        {designStudioHours.map(
          (h, index) =>
            h.hours && (
              <div
                key={index}
                className={cx(
                  " w-full flex flex-row px-2 font-graphie text-mattamy-blue text-sm  ",
                  {
                    "mb-4": index === designStudioHours.length - 1,
                  }
                )}
              >
                <div className="w-3/12 font-bold text-right">{h.day}</div>
                <div className="w-9/12 ml-4 text-left">
                  {h.hours && h.hours.length > 10 && (
                    <span className="text-left font-graphie whitespace-no-wrap">
                      {h.hours}
                    </span>
                  )}
                  {h.displayText && h.displayText.length > 3 && (
                    <span className="text-left font-graphie whitespace-no-wrap">
                      {h.displayText}
                    </span>
                  )}
                </div>
              </div>
            )
        )}
      </>
    </animated.div>
  );
  const hasDesingStudioHours = designStudioHours.filter((h) => h.hours);
  return (
    <CardContainer>
      <CardTitle
        tabIndex={0}
        className="font-trade-gothic-20 text-mattamy-blue text-3xl leading-8 md:mt-1 mb-6 block"
      >
        {designStudioName}
      </CardTitle>
      <Card className="w-full bg-mattamy-white flex flex-col lg:flex-row justify-left items-start">
        {description && (
          <div className="w-full h-full text-left relative rounded overflow-hidden md:w-4/12">
            <div className="overflow-hidden text-mattamy-blue leading-7 font-graphie mb-2">
              {description}
            </div>
          </div>
        )}

        <div className="w-full h-full text-left md:w-8/12 flex flex-col">
          <div className="flex flex-col md:flex-row">
            <ProductInfo className="pt-2 pb-4 lg:pt-0 w-full md:w-1/2 flex flex-col justify-between">
              <div className="block lg:pl-5 grid grid-cols-2 gap-4">
                {direction && (
                  <CardDescription className="my-2 flex items-center break-all bg-action-blue text-white rounded py-3 justify-left">
                    <IconContainer aria-hidden="true" className="bg-white">
                      <MapMarker className="h-4 w-4 text-action-blue" />
                    </IconContainer>
                    <a
                      href={
                        gmbLink && gmbLink.href
                          ? gmbLink.href
                          : getMapLink(
                              direction +
                                " " +
                                city +
                                " " +
                                state +
                                " " +
                                zipCode
                            )
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={
                        "Go to " +
                        direction +
                        " " +
                        city +
                        " " +
                        state +
                        " " +
                        zipCode +
                        ". Opens in GoogleMaps"
                      }
                    >
                      <h3 className="text-white leading-7 font-graphie">
                        {"Directions"}
                      </h3>
                    </a>
                  </CardDescription>
                )}
                {emailAddress && (
                  <CardDescription className="my-2 flex items-center break-all bg-action-blue text-white rounded py-3 justify-left">
                    <IconContainer aria-hidden="true">
                      <Mail className="h-4 w-4" />
                    </IconContainer>
                    <a
                      className="text-white leading-7 font-graphie"
                      href={`mailto:${emailAddress}`}
                      aria-label={"Email to " + emailAddress}
                    >
                      {"Email"}
                    </a>
                  </CardDescription>
                )}
                {phone && (
                  <CardDescription className="my-2 flex items-center break-all bg-action-blue text-white rounded py-3 justify-left">
                    <div
                      aria-hidden="true"
                      className="flex justify-center items-center w-30-mattamy h-30-mattamy bg-action-blue inline ml-4 mr-2 rounded-full text-white"
                    >
                      <CallIcon className="w-30-mattamy h-30-mattamy " />
                    </div>
                    <a
                      className="text-white leading-7 font-graphie"
                      href={`tel:${phone}`}
                      aria-label={"Call to " + phone}
                    >
                      {"Call"}
                    </a>
                  </CardDescription>
                )}
              </div>
            </ProductInfo>

            <ProductInfo className="pt-2 lg:pt-0 w-full md:w-1/2 flex flex-col justify-start md:ml-6">
              {hasDesingStudioHours.length > 1 && (
                <>
                  <CardDescription>
                    <IconContainer>
                      <Calendar className="mt-1 h-4 w-4" />
                    </IconContainer>
                    <span className="mt-1 justify-center items-center place-self-center mr-2">
                      {"Hours:"}
                    </span>
                    <button
                      className="flex justify-center items-center place-self-center"
                      onClick={() => {
                        toggleScheduleOpen(!scheduleOpen);
                      }}
                      aria-label={
                        scheduleOpen ? "Close Schedule" : "Show Schedule"
                      }
                      aria-expanded={scheduleOpen}
                    >
                      <p className="break-all mt-1">
                        {openNow ? (
                          <>
                            <span className="text-action-blue font-bold">
                              {"Open Now - "}
                            </span>
                            <span>{"Closes at " + getClosedHour()}</span>
                          </>
                        ) : (
                          <span className="text-action-blue font-bold">
                            {"Closed Now"}
                          </span>
                        )}
                      </p>
                      <ToggleScheduleContainer
                        tabIndex={0}
                        className=" place-self-center"
                        //as="button"
                      >
                        <svg
                          className={cx(
                            "mt-1 md:mt-0 w-6 h-6 text-action-blue md:text-mattamy-blue transform",
                            {
                              "rotate-90": scheduleOpen,
                              "-rotate-90": !scheduleOpen,
                            }
                          )}
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fill="currentColor"
                            d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                          />
                        </svg>
                      </ToggleScheduleContainer>
                    </button>
                  </CardDescription>
                  <CardDescription>
                    <div className="w-full z-20">
                      {scheduleOpen && (
                        <animated.div style={expand}>
                          {renderSchedule()}
                        </animated.div>
                      )}
                    </div>
                  </CardDescription>
                </>
              )}

              {ctaText && (
                <StyledButton
                  color={"action-blue"}
                  variant="filled"
                  className="block text-base mt-6 pt-0 pb-0 md:w-full"
                  href={ctaurl && ctaurl.href}
                >
                  {ctaText}
                </StyledButton>
              )}
            </ProductInfo>
          </div>
        </div>
      </Card>
    </CardContainer>
  );
};

DesingCenterCommunityCardMobile.propTypes = {
  props: PropTypes.object,
};

export default DesingCenterCommunityCardMobile;
